import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import { material } from '@trashlab/api/src/domains/pricing/schemas/material';
import { price } from '@trashlab/api/src/domains/pricing/schemas/price';
import { product } from '@trashlab/api/src/domains/pricing/schemas/product';
import { recurrence_settings } from '@trashlab/api/src/domains/orders/schemas/recurrence-settings';
import {
  AnyPgColumn,
  boolean,
  doublePrecision,
  foreignKey,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
// removed recurring configuration imports; reverting to existing models

/**
 * Enum defining the categories of pricing for different service types.
 * - ROLLOFF_SERVICE: Standard roll-off container services
 * - ROLLOFF_ADDITIONAL_FEE: Extra fees associated with roll-off services
 * - PORTABLE_TOILET: Portable toilet rental and service
 */
export const pricing_category = pgEnum('pricing_category', [
  'ROLLOFF_SERVICE',
  'ROLLOFF_ADDITIONAL_FEE',
  'PORTABLE_TOILET',
]);

export const price_card_status = pgEnum('price_card_status', [
  'ACTIVE',
  'INACTIVE',
  'DRAFT',
]);

/**
 * Enum defining how tonnage overage is rounded when calculating additional ton fees.
 * - PROPORTIONAL: Charge for exact overage (e.g. 0.5 tons over × $30/ton = $15)
 * - ROUND_UP: Round overage up to the next whole ton (e.g. 0.5 tons over → 1 ton × $30 = $30)
 */
export const tonnage_rounding_mode = pgEnum('tonnage_rounding_mode', [
  'PROPORTIONAL',
  'ROUND_UP',
]);

/**
 * Enum defining the weight unit used for pricing on a price card.
 * Determines how tonsLimit and additionalTonsPrice are interpreted.
 * - TONS: US short tons (default, backward compatible)
 * - METRIC_TONS: Metric tonnes (1000 kg)
 * - KGS: Kilograms
 */
export const pricing_weight_unit = pgEnum('pricing_weight_unit', [
  'TONS',
  'METRIC_TONS',
  'KGS',
]);

/**
 * Represents a Stock Keeping Unit (SKU) for pricing and product management.
 * Version 2 of the SKU schema with enhanced pricing options and service-specific fields.
 * Supports various pricing models including delivery, pickup, recurring charges, and additional fees.
 */
export const sku_v2 = pgTable(
  'sku_v2',
  {
    ...defaultFields.fieldsWithTenantId(),
    status: price_card_status('status').default('ACTIVE'),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** Service category this SKU belongs to */
    category: pricing_category('category').notNull(),
    // Saved component semantics survive switching back to the legacy editor.
    unifiedCharges: boolean('unifiedCharges').notNull().default(false),
    /** Optional custom name for the SKU */
    customName: varchar('customName', { length: 255 }),
    /** Reference to custom pricing if applicable */
    customPriceId: uuid('customPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to delivery service pricing */
    deliveryPriceId: uuid('deliveryPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to respot service pricing */
    respotPriceId: uuid('respotPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to migrate service pricing */
    migratePriceId: uuid('migratePriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to container exchange service pricing */
    exchangePriceId: uuid('exchangePriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to pickup service pricing */
    pickupPriceId: uuid('pickupPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to live load service pricing */
    liveloadPriceId: uuid('liveloadPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to pricing for additional rental days */
    additionalDaysPriceId: uuid('additionalDaysPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to pricing for additional tonnage */
    additionalTonsPriceId: uuid('additionalTonsPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Maximum tonnage/weight before additional fees apply (interpreted in weightUnit) */
    tonsLimit: doublePrecision('tonsLimit').default(0),
    /** How weight overage is rounded (PROPORTIONAL = exact, ROUND_UP = next whole unit) */
    tonnageRoundingMode: tonnage_rounding_mode('tonnageRoundingMode').default(
      'PROPORTIONAL',
    ),
    /** When true, always generate a tonnage line item on pickup even if the amount is $0 (no overage) */
    alwaysShowTonnageLineItem: boolean('alwaysShowTonnageLineItem'),
    /** Weight unit for pricing: determines how tonsLimit and additionalTonsPrice are interpreted */
    weightUnit: pricing_weight_unit('weightUnit').default('TONS'),
    /** Maximum rental days before additional fees apply */
    daysLimit: integer('daysLimit').default(0),
    /** Optional customer site this SKU is specific to */
    customerSiteId: uuid('customerSiteId'),
    /** Optional customer this SKU is specific to */
    customerId: uuid('customerId'),
    /** Price card this card was copied from, when applicable. */
    basedOnId: uuid('basedOnId').references(
      (): AnyPgColumn => sku_v2.id,
    ),
    /** Display name for the SKU */
    name: text('name').default('').notNull(),
    /** Optional customer-facing name for documents and customer experiences. */
    customerFacingName: varchar('customerFacingName', { length: 255 }),
    /** Whether this SKU can be ordered online */
    onlineOrderEnabled: boolean('onlineOrderEnabled').default(false).notNull(),
    /** Reference to dumpster rental pricing */
    dumpsterPriceId: uuid('dumpsterPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to recurring service pricing */
    // This is deprecated, DO NOT USE THIS.
    recurringPriceId: uuid('recurringPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to dry run service pricing */
    dryRunPriceId: uuid('dryRunPriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    /** Description text for online ordering */
    onlineOrderCopy: text('onlineOrderCopy').notNull().default(''),
    /** Reference to on-demand service pricing */
    onDemandServicePriceId: uuid('onDemandServicePriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Reference to minimum tonnage fee pricing */
    minTonnageFeePriceId: uuid('minTonnageFeePriceId').references(
      (): AnyPgColumn => price.id,
    ),
    /** Whether to start counting rental from delivery date or day after */
    startRentalFromDeliveryDate: boolean('startRentalFromDeliveryDate')
      .default(false)
      .notNull(),
    /** UUID of the subscription settings that define billing behavior for this SKU */
    subscriptionSettingsId: uuid('subscriptionSettingsId'),
    /** UUID of the recurrence settings */
    recurrenceSettingsId: uuid('recurrenceSettingsId').references(
      (): AnyPgColumn => recurrence_settings.id,
    ),
  },
  (table) => {
    return {
      site_merge_page_idx: index('sku_v2_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      catalog_active_page_idx: index('sku_v2_catalog_active_page_idx')
        .on(table.tenantId, table.createdAtTs.desc(), table.id.desc())
        .where(sql`${table.archivedAtTs} IS NULL AND ${table.status} = 'ACTIVE'`),
      createdAtTs_idx: index('sku_v2_createdAtTs_idx').on(table.createdAtTs),
      tenantId_idx: index('sku_v2_tenantId_idx').on(table.tenantId),
      tenantId_id_idx: index('sku_v2_tenant_id_idx').on(
        table.tenantId,
        table.id,
      ),
      updatedAtTs_idx: index('sku_v2_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('sku_v2_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('sku_v2_versionNumber_idx').on(
        table.versionNumber,
      ),
      customPriceId_idx: index('sku_v2_customPriceId_idx').on(
        table.customPriceId,
      ),
      deliveryPriceId_idx: index('sku_v2_deliveryPriceId_idx').on(
        table.deliveryPriceId,
      ),
      respotPriceId_idx: index('sku_v2_respotPriceId_idx').on(
        table.respotPriceId,
      ),
      migratePriceId_idx: index('sku_v2_migratePriceId_idx').on(
        table.migratePriceId,
      ),
      exchangePriceId_idx: index('sku_v2_exchangePriceId_idx').on(
        table.exchangePriceId,
      ),
      pickupPriceId_idx: index('sku_v2_pickupPriceId_idx').on(
        table.pickupPriceId,
      ),
      liveloadPriceId_idx: index('sku_v2_liveloadPriceId_idx').on(
        table.liveloadPriceId,
      ),
      additionalDaysPriceId_idx: index('sku_v2_additionalDaysPriceId_idx').on(
        table.additionalDaysPriceId,
      ),
      additionalTonsPriceId_idx: index('sku_v2_additionalTonsPriceId_idx').on(
        table.additionalTonsPriceId,
      ),
      customerSiteId_idx: index('sku_v2_customerSiteId_idx').on(
        table.customerSiteId,
      ),
      customerId_idx: index('sku_v2_customerId_idx').on(table.customerId),
      basedOnId_idx: index('sku_v2_basedOnId_idx').on(table.basedOnId),
      customer_merge_page_idx: index('sku_v2_customer_merge_page_idx').on(
        table.tenantId,
        table.customerId,
        table.id,
      ),
      dumpsterPriceId_idx: index('sku_v2_dumpsterPriceId_idx').on(
        table.dumpsterPriceId,
      ),
      recurringPriceId_idx: index('sku_v2_recurringPriceId_idx').on(
        table.recurringPriceId,
      ),
      dryRunPriceId_idx: index('sku_v2_dryRunPriceId_idx').on(
        table.dryRunPriceId,
      ),
      onDemandServicePriceId_idx: index('sku_v2_onDemandServicePriceId_idx').on(
        table.onDemandServicePriceId,
      ),
      minTonnageFeePriceId_idx: index('sku_v2_minTonnageFeePriceId_idx').on(
        table.minTonnageFeePriceId,
      ),
      subscriptionSettingsId_idx: index('sku_v2_subscriptionSettingsId_idx').on(
        table.subscriptionSettingsId,
      ),
      // Composite index for listSkus query - optimizes the base filter pattern
      // Order: tenantId (most selective, always filtered) -> status (always filtered) -> archivedAtTs (always IS NULL)
      // This index helps when orWhereClauses is empty (less common but still happens)
      tenantId_status_archivedAtTs_idx: index(
        'sku_v2_tenantId_status_archivedAtTs_idx',
      ).on(table.tenantId, table.status, table.archivedAtTs),
      // Composite index for listSkus query - includes createdAtTs for ORDER BY optimization
      // This allows PostgreSQL to use a single index for both filtering AND sorting
      tenantId_status_archivedAtTs_createdAtTs_idx: index(
        'sku_v2_tenantId_status_archivedAtTs_createdAtTs_idx',
      ).on(table.tenantId, table.status, table.archivedAtTs, table.createdAtTs),
      // Indexes for OR clause branches - helps PostgreSQL use bitmap index scans
      // When orWhereClauses contains customerId filter
      tenantId_customerId_idx: index('sku_v2_tenantId_customerId_idx').on(
        table.tenantId,
        table.customerId,
      ),
      // When orWhereClauses contains customerSiteId filter
      tenantId_customerSiteId_idx: index(
        'sku_v2_tenantId_customerSiteId_idx',
      ).on(table.tenantId, table.customerSiteId),
      // The primary key (id) already has an index, so that's covered

      sku_v2_customerId_fkey: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'sku_v2_customerId_fkey',
      }),
      sku_v2_customerSiteId_fkey: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'sku_v2_customerSiteId_fkey',
      }),
      // sku_v2_customPriceId_fkey: foreignKey({
      //   columns: [table.customPriceId],
      //   foreignColumns: [price.id as AnyPgColumn],
      //   name: 'sku_v2_customPriceId_fkey',
      // }),
      // sku_v2_deliveryPriceId_fkey: foreignKey({
      //   columns: [table.deliveryPriceId],
      //   foreignColumns: [price.id as AnyPgColumn],
      //   name: 'sku_v2_deliveryPriceId_fkey',
      // }),
      // sku_v2_dryRunPriceId_fkey: foreignKey({
      //   columns: [table.dryRunPriceId],
      //   foreignColumns: [price.id as AnyPgColumn],
      //   name: 'sku_v2_dryRunPriceId_fkey',
      // }),
      // sku_v2_dumpsterPriceId_fkey: foreignKey({
      //   columns: [table.dumpsterPriceId],
      //   foreignColumns: [price.id as AnyPgColumn],
      //   name: 'sku_v2_dumpsterPriceId_fkey',
      // }),
      // sku_v2_exchangePriceId_fkey: foreignKey({
      //   columns: [table.exchangePriceId],
      //   foreignColumns: [price.id as AnyPgColumn],
      //   name: 'sku_v2_exchangePriceId_fkey',
      // }),
      // sku_v2_liveloadPriceId_fkey: foreignKey({
      //   columns: [table.liveloadPriceId],
      //   foreignColumns: [price.id as AnyPgColumn],
      //   name: 'sku_v2_liveloadPriceId_fkey',
      // }),
      // sku_v2_pickupPriceId_fkey: foreignKey({
      //   columns: [table.pickupPriceId],
      //   foreignColumns: [price.id as AnyPgColumn],
      //   name: 'sku_v2_pickupPriceId_fkey',
      // }),
      // sku_v2_recurringPriceId_fkey: foreignKey({
      //   columns: [table.recurringPriceId],
      //   foreignColumns: [price.id as AnyPgColumn],
      //   name: 'sku_v2_recurringPriceId_fkey',
      // }),
      // sku_v2_onDemandServicePriceId_fkey: foreignKey({
      //   columns: [table.onDemandServicePriceId],
      //   foreignColumns: [price.id],
      //   name: 'sku_v2_onDemandServicePriceId_fkey',
      // }),
      // sku_v2_subscriptionSettingsId_fkey: foreignKey({
      //   columns: [table.subscriptionSettingsId],
      //   foreignColumns: [subscription_settings.id],
      //   name: 'sku_v2_subscriptionSettingsId_fkey',
      // }),
    };
  },
);

export const sku_v2_material = pgTable(
  'sku_v2_material',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    skuId: uuid('skuId').notNull(),
    materialId: uuid('materialId').notNull(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('sku_v2_material_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      material_id_idx: index('sku_v2_material_material_id_idx').on(
        table.materialId,
      ),
      sku_id_idx: index('sku_v2_material_sku_id_idx').on(table.skuId),
      sku_id_material_id_idx: index(
        'sku_v2_material_sku_id_material_id_idx',
      ).on(table.skuId, table.materialId),
      tenantId_idx: index('sku_v2_material_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('sku_v2_material_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('sku_v2_material_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('sku_v2_material_versionNumber_idx').on(
        table.versionNumber,
      ),

      //foreign keys
      sku_v2_material_materialId_fkey: foreignKey({
        columns: [table.materialId],
        foreignColumns: [material.id],
        name: 'sku_v2_material_materialId_fkey',
      }),
      sku_v2_material_skuId_fkey: foreignKey({
        columns: [table.skuId],
        foreignColumns: [sku_v2.id],
        name: 'sku_v2_material_skuId_fkey',
      }),
    };
  },
);

export const sku_v2_material_history = pgTable('sku_v2_material_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  lastModifiedBy: uuid('lastModifiedBy'),
  skuId: uuid('skuId'),
  materialId: uuid('materialId'),
});

/**
 * Junction table linking SKUs to pricing zones.
 * Allows each SKU to be associated with multiple geographic pricing zones.
 * Used to implement zone-based pricing strategies.
 */
export const sku_v2_zone = pgTable(
  'sku_v2_zone',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** Reference to the SKU */
    skuId: uuid('skuId').notNull(),
    /** Reference to the pricing zone */
    zoneId: uuid('zoneId').notNull(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('sku_v2_zone_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('sku_v2_zone_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('sku_v2_zone_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('sku_v2_zone_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('sku_v2_zone_versionNumber_idx').on(
        table.versionNumber,
      ),
      skuId_idx: index('sku_v2_zone_skuId_idx').on(table.skuId),
      zoneId_idx: index('sku_v2_zone_zoneId_idx').on(table.zoneId),
      // Covering index for list-skus query to enable index-only scans
      skuId_zoneId_covering_idx: index(
        'sku_v2_zone_skuId_zoneId_covering_idx',
      ).on(table.skuId, table.zoneId),

      //foreign keys
      sku_v2_zone_skuId_fkey: foreignKey({
        columns: [table.skuId],
        foreignColumns: [sku_v2.id],
        name: 'sku_v2_zone_skuId_fkey',
      }),
      sku_v2_zone_zoneId_fkey: foreignKey({
        columns: [table.zoneId],
        foreignColumns: [price_zone.id],
        name: 'sku_v2_zone_zoneId_fkey',
      }),
    };
  },
);

/**
 * Historical record of changes to SKU-zone associations.
 * Tracks all modifications to SKU-zone relationships over time for auditing and versioning.
 */
export const sku_v2_zone_history = pgTable('sku_v2_zone_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  lastModifiedBy: uuid('lastModifiedBy'),
  skuId: uuid('skuId'),
  zoneId: uuid('zoneId'),
});

export const sku_v2_product = pgTable(
  'sku_v2_product',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    skuId: uuid('skuId').notNull(),
    productId: uuid('productId').notNull(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('sku_v2_product_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      product_id_idx: index('sku_v2_product_product_id_idx').on(
        table.productId,
      ),
      sku_id_idx: index('sku_v2_product_sku_id_idx').on(table.skuId),
      sku_id_product_id_idx: index('sku_v2_product_sku_id_product_id_idx').on(
        table.skuId,
        table.productId,
      ),
      tenantId_idx: index('sku_v2_product_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('sku_v2_product_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('sku_v2_product_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('sku_v2_product_versionNumber_idx').on(
        table.versionNumber,
      ),

      //foreign keys
      sku_v2_product_productId_fkey: foreignKey({
        columns: [table.productId],
        foreignColumns: [product.id],
        name: 'sku_v2_product_productId_fkey',
      }),
      sku_v2_product_skuId_fkey: foreignKey({
        columns: [table.skuId],
        foreignColumns: [sku_v2.id],
        name: 'sku_v2_product_skuId_fkey',
      }),
    };
  },
);

export const sku_v2_product_history = pgTable('sku_v2_product_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  lastModifiedBy: uuid('lastModifiedBy'),
  skuId: uuid('skuId'),
  productId: uuid('productId'),
});

export const priceZoneTypeEnum = pgEnum('price_zone_type', [
  'ZIP_CODE',
  'RADIUS_BASED',
  'GEO_AREA',
]);

export const price_zone = pgTable(
  'price_zone',
  {
    ...defaultFields.fieldsWithTenantId(),
    name: varchar('name', { length: 256 }).notNull(),
    zipCodes: varchar('zipCodes', { length: 11 }).array(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    priorityIndex: integer('priorityIndex'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    latitude: doublePrecision('latitude'),
    longitude: doublePrecision('longitude'),
    minRadius: doublePrecision('minRadius'),
    radius: doublePrecision('radius'),
    type: priceZoneTypeEnum('type').default('ZIP_CODE').notNull(),
    color: varchar('color', { length: 7 }),
  },
  (table) => {
    return {
      createdAtTs_idx: index('price_zone_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('price_zone_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('price_zone_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('price_zone_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('price_zone_versionNumber_idx').on(
        table.versionNumber,
      ),
    };
  },
);

export const price_zone_history = pgTable('price_zone_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  versionId: uuid('versionId'),
  name: varchar('name', { length: 256 }),
  zipCodes: varchar('zipCodes', { length: 11 }).array(),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  priorityIndex: integer('priorityIndex'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  latitude: doublePrecision('latitude'),
  longitude: doublePrecision('longitude'),
  minRadius: doublePrecision('minRadius'),
  radius: doublePrecision('radius'),
  type: priceZoneTypeEnum('type'),
  color: varchar('color', { length: 7 }),
});

export type PriceZoneInsertSchema = typeof price_zone.$inferInsert;
